import { api, enviarFormulario } from '../api';
/* O mesmo limite do recebimento, e do mesmo lugar: dois números iguais
   escritos em dois arquivos são dois números que um dia divergem. */
import { LIMITE_POR_ENVIO } from '../Fotos';

/**
 * A fotografia de um registro técnico — um ensaio ou um dia de campo.
 *
 * O adensamento e o módulo de resiliência têm rotas próprias, que
 * devolvem o ensaio inteiro recarregado porque as telas deles esperam
 * isso de toda gravação. Estas aqui devolvem só a lista de fotos: as
 * telas que as usam têm um bloco de fotos que se recarrega sozinho, sem
 * remontar a página em volta.
 *
 * O `base` é o caminho do dono — `/ensaios/:id/evidencias` ou
 * `/diarios/:id/evidencias`. Os dois literais aparecem escritos nas
 * telas que os usam, que é onde o teste 26 os procura.
 */

export type TipoDeFoto = 'cp_antes' | 'cp_depois' | 'moldagem' | 'montagem'
  | 'ruptura' | 'equipamento' | 'outro';

export interface FotoDeEnsaio {
  id: string;
  tipo: TipoDeFoto;
  legenda: string | null;
  ordem: number;
  desconsiderada: boolean;
  motivo_desconsideracao: string | null;
  criado_por_nome: string | null;
  criado_em: string;
  arquivo_chave: string;
  arquivo_nome: string | null;
  corpo_prova_ordem: number | null;
}

/**
 * O que a rota devolve.
 *
 * O código do MÉTODO vem junto porque é ele que escolhe o gabarito de
 * enquadramento, e a tela não o tem em mãos — carregar a ficha do ensaio
 * só para descobrir o gabarito seria uma segunda chamada por bloco.
 */
export interface Evidencias {
  /** Só o dono ENSAIO responde isto — é ele que escolhe o gabarito. */
  metodo_codigo?: string | null;
  fotos: FotoDeEnsaio[];
}

/**
 * As levas.
 *
 * O servidor recusa a chamada INTEIRA a partir da décima primeira foto,
 * e recusa em inglês — nenhuma sobe e a mensagem não diz por quê. Em
 * série e não em paralelo: quem fotografa está no laboratório ou no
 * canteiro com a rede da casa, e envios simultâneos de foto de celular é
 * como metade falha por tempo esgotado.
 *
 * O CAMINHO não entra aqui: ele é escrito por extenso em cada função
 * pública abaixo, ao lado da chamada que o usa. É o contrato entre a
 * tela e a API, e é assim que o teste 26 o encontra — caminho montado a
 * partir de um parâmetro existe só em tempo de execução, e o que não se
 * acha procurando não se confere.
 */
async function emLevas(
  fotos: { arquivo: File; tipo: TipoDeFoto; legenda?: string }[],
  aoProgredir: ((f: number) => void) | undefined,
  enviar: (form: FormData, p: (f: number) => void) => Promise<Evidencias>,
): Promise<Evidencias> {
  const blocos: typeof fotos[] = [];
  for (let i = 0; i < fotos.length; i += LIMITE_POR_ENVIO) {
    blocos.push(fotos.slice(i, i + LIMITE_POR_ENVIO));
  }
  let ultimo: Evidencias = { fotos: [] };
  for (let b = 0; b < blocos.length; b += 1) {
    const form = new FormData();
    for (const f of blocos[b]) form.append('fotos', f.arquivo, f.arquivo.name);
    form.append('itens', JSON.stringify(blocos[b].map((f) => ({
      tipo: f.tipo, legenda: f.legenda || undefined,
    }))));
    // eslint-disable-next-line no-await-in-loop
    ultimo = await enviar(form, (fr) => aoProgredir?.((b + fr) / blocos.length));
  }
  return ultimo;
}

// ── do ensaio ────────────────────────────────────────────────────────

export const verEvidenciasDoEnsaio = (id: string) =>
  api<Evidencias>(`/ensaios/${id}/evidencias`);

export const enviarEvidenciasDoEnsaio = (
  id: string,
  fotos: { arquivo: File; tipo: TipoDeFoto; legenda?: string }[],
  aoProgredir?: (f: number) => void,
) => emLevas(fotos, aoProgredir, (form, p) =>
  enviarFormulario<Evidencias>(`/ensaios/${id}/evidencias`, form, p));

export const desconsiderarEvidenciaDoEnsaio = (
  id: string, evid: string, motivo: string,
) => api<Evidencias>(`/ensaios/${id}/evidencias/${evid}/desconsiderar`,
  { metodo: 'POST', corpo: { motivo } });

// ── do dia de campo ──────────────────────────────────────────────────

export const verEvidenciasDoDiario = (id: string) =>
  api<Evidencias>(`/diarios/${id}/evidencias`);

export const enviarEvidenciasDoDiario = (
  id: string,
  fotos: { arquivo: File; tipo: TipoDeFoto; legenda?: string }[],
  aoProgredir?: (f: number) => void,
) => emLevas(fotos, aoProgredir, (form, p) =>
  enviarFormulario<Evidencias>(`/diarios/${id}/evidencias`, form, p));

export const desconsiderarEvidenciaDoDiario = (
  id: string, evid: string, motivo: string,
) => api<Evidencias>(`/diarios/${id}/evidencias/${evid}/desconsiderar`,
  { metodo: 'POST', corpo: { motivo } });
